import React, { useState, useEffect, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { cancelTaskLoadingSelector, cancelTaskErrorSelector, cancelTaskSuccessSelector } from '@redux/tasks/tasksSelectors';
import { patchCancelTaskThunk } from '@redux/tasks/tasksThunks';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { WarningCircleIcon } from 'components/Icons';

export type CancelTaskModalProps = {
  visible: boolean;
  onClosePress: () => void;
  onConfirmPress?: () => void;
  onSuccess?: () => void;
  taskId?: string;
  isSingleCancel?: boolean;
};

const MESSAGES = {
  single: {
    title: 'Cancel task',
    description: 'You sure you want to cancel this task? This action cannot be undone.',
    proceedText: 'Cancel task',
    closeText: 'Keep task',
    success: 'Task was cancelled successfully!',
    error: 'Something went wrong while cancelling the task. Please try again.'
  },
  multiple: {
    title: 'Cancel tasks',
    description: 'You sure you want to cancel these tasks? This action cannot be undone.',
    proceedText: 'Cancel tasks',
    closeText: 'Keep tasks',
    success: 'Tasks were cancelled successfully!',
    error: 'Something went wrong while cancelling the tasks. Please try again.'
  }
};

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & CancelTaskModalProps;

const CancelTaskModal: React.FC<Props> = ({
  taskId,
  visible,
  onConfirmPress,
  onClosePress,
  onSuccess,
  cancelTaskCallback,
  isSingleCancel = true,
  cancelTaskLoading,
  cancelTaskError,
  cancelTaskSuccess
}) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const messages = isSingleCancel ? MESSAGES.single : MESSAGES.multiple;
  const { showAlertInfo, showAlertWarning } = useAlert();
  const handledRef = useRef<boolean>(false);

  // Reset processing state and handled flag when modal is closed
  useEffect(() => {
    if (!visible) {
      setIsProcessing(false);
      handledRef.current = false;
    }
  }, [visible]);

  // Monitor Redux state changes to handle success/error
  useEffect(() => {
    if (isProcessing && !handledRef.current) {
      if (cancelTaskSuccess) {
        // Task was successfully cancelled - mark as handled immediately to prevent re-execution
        handledRef.current = true;
        onConfirmPress?.();
        onSuccess?.();
        showAlertInfo({
          message: messages.success
        });
        setIsProcessing(false);
        // Close modal after success
        onClosePress();
      } else if (cancelTaskError) {
        // Task cancellation failed - mark as handled immediately to prevent re-execution
        handledRef.current = true;
        showAlertWarning({
          message: messages.error
        });
        setIsProcessing(false);
        // Close modal after error
        onClosePress();
        console.log('Failed to cancel task', cancelTaskError);
      }
    }
  }, [cancelTaskSuccess, cancelTaskError, isProcessing, onConfirmPress, onSuccess, onClosePress, messages, showAlertInfo, showAlertWarning]);

  const onConfirm = () => {
    if (!taskId) {
      console.log('Task ID is not provided');
      return;
    }

    setIsProcessing(true);
    cancelTaskCallback(taskId, isSingleCancel);
  };

  return (
    <ConfirmationDialogV2
      open={visible}
      icon={WarningCircleIcon}
      title={messages.title}
      description={messages.description}
      proceedEvent={onConfirm}
      proceedEventText={messages.proceedText}
      proceedLoading={isProcessing}
      closeEvent={onClosePress}
      closeEventText={messages.closeText}
    />
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    cancelTaskLoading: cancelTaskLoadingSelector(state),
    cancelTaskError: cancelTaskErrorSelector(state),
    cancelTaskSuccess: cancelTaskSuccessSelector(state)
  }),
  {
    cancelTaskCallback: patchCancelTaskThunk
  }
);

export default compose(connectRedux)(CancelTaskModal);
